/**
 * Cross-audit chart slot ↔ nursing multi-chart index (Thai titles like "Chart ตรวจไขว้ 1v1").
 * Used by submission hydration merge; keep pure for unit tests.
 */

export type CrossAuditSlotDoc = Record<string, unknown>;

/**
 * Default nursing layout (single source of truth; ComplianceHub clones for UI defaults).
 * Used when a thin parent `nurse_notes` doc has `submitted` / cross-audit flags but omits `chartTitles`
 * — otherwise `findChartIndexByCrossAuditSlot` returns -1 and row merge never runs (Submitted + empty NA).
 */
export const DEFAULT_NURSING_LAYOUT_TITLES: readonly string[] = [
  'Chart รายโรค 1',
  'Chart รายโรค 2',
  'Chart รายโรค 3',
  'Chart โรคทั่วไป 1',
  'Chart โรคทั่วไป 2',
  'Chart ตรวจไขว้ 1',
  'Chart ตรวจไขว้ 2',
  'Chart ตรวจไขว้ 3',
];

/** Map crossAuditSlot (1,2,3) to chart index using chartTitles (e.g. "Chart ตรวจไขว้ 1" -> index). */
export const findChartIndexByCrossAuditSlot = (chartTitles: string[], slot: number): number => {
  const slotStr = String(slot);
  return chartTitles.findIndex((title) => {
    const m = title.match(/ตรวจไขว้\s*(\d+)/);
    return m ? m[1] === slotStr : false;
  });
};

/**
 * Resolve chart index for a cross-audit slot when live `chartTitles` are missing or incomplete
 * (common after parent-only Firestore sync). Fills gaps from {@link DEFAULT_NURSING_LAYOUT_TITLES}.
 */
export const findChartIndexByCrossAuditSlotWithFallback = (
  chartTitles: string[],
  slot: number,
  chartCount: number,
): number => {
  const direct = findChartIndexByCrossAuditSlot(chartTitles, slot);
  if (direct >= 0) return direct;
  if (!Number.isFinite(slot) || slot < 1) return -1;

  const len = Math.max(
    chartCount,
    chartTitles.length,
    DEFAULT_NURSING_LAYOUT_TITLES.length,
  );
  const merged: string[] = [];
  for (let i = 0; i < len; i++) {
    const t = chartTitles[i];
    merged.push(t && String(t).trim() ? String(t) : (DEFAULT_NURSING_LAYOUT_TITLES[i] ?? ''));
  }
  return findChartIndexByCrossAuditSlot(merged, slot);
};

export const extractCrossAuditSlotFromDoc = (doc: CrossAuditSlotDoc): number | null => {
  const direct = doc.crossAuditSlot;
  if (typeof direct === 'number' && Number.isFinite(direct) && direct >= 1) {
    return direct;
  }

  const candidates = [
    doc.category,
    doc.categoryId,
    doc.categoryDocId,
    doc.title,
    doc.sourceCategoryTitle,
  ];

  for (const value of candidates) {
    if (typeof value !== 'string') continue;
    const fromCategory = value.match(/\*\*(\d+)$/);
    if (fromCategory) {
      const parsed = Number.parseInt(fromCategory[1], 10);
      if (Number.isFinite(parsed) && parsed >= 1) return parsed;
    }
    const fromThaiTitle = value.match(/ตรวจไขว้\s*(\d+)/);
    if (fromThaiTitle) {
      const parsed = Number.parseInt(fromThaiTitle[1], 10);
      if (Number.isFinite(parsed) && parsed >= 1) return parsed;
    }
  }

  return null;
};
